
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getSMSLogs } from "@/utils/smsUtils";
import { Lead } from "@/context/LeadContext";
import { format, parseISO } from "date-fns";
import { el } from "date-fns/locale";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

interface PartnerSMSMetricsProps {
  partnerId: string;
  leads: Lead[];
}

const PartnerSMSMetrics: React.FC<PartnerSMSMetricsProps> = ({ partnerId, leads }) => {
  const [smsLogs, setSmsLogs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<{
    totalSent: number;
    totalClicked: number;
    clickRate: number;
    byDate: any[];
  }>({
    totalSent: 0,
    totalClicked: 0,
    clickRate: 0,
    byDate: [],
  });

  useEffect(() => {
    const fetchSMSLogs = async () => {
      try {
        setIsLoading(true);
        const logs = await getSMSLogs(partnerId);
        
        // Filter logs to ensure they belong to this partner's leads
        const leadIds = leads.map(lead => lead.id);
        const relevantLogs = logs.filter(log => 
          (log.partnerId === partnerId || log.partnerId === "" || log.partnerId === null) && 
          leadIds.includes(log.leadId)
        );
        
        setSmsLogs(relevantLogs);
        
        // Calculate metrics
        const sentMessages = relevantLogs.filter(log => log.status === "sent" || log.messageId);
        const clickedMessages = relevantLogs.filter(log => log.event === "click");
        
        // Group by date for chart
        const messagesByDate = sentMessages.reduce((acc: any, log: any) => {
          const date = log.timestamp.substring(0, 10);
          if (!acc[date]) {
            acc[date] = { sent: 0, clicked: 0 };
          }
          acc[date].sent += 1;
          return acc;
        }, {});
        
        // Add click data to the date groups
        clickedMessages.forEach((log: any) => {
          const date = log.timestamp.substring(0, 10);
          if (messagesByDate[date]) {
            messagesByDate[date].clicked += 1;
          }
        });
        
        const chartData = Object.keys(messagesByDate).map(date => ({
          date,
          sent: messagesByDate[date].sent,
          clicked: messagesByDate[date].clicked,
        }));
        
        // Sort by date
        chartData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        
        setMetrics({
          totalSent: sentMessages.length,
          totalClicked: clickedMessages.length,
          clickRate: sentMessages.length > 0 ? Math.round((clickedMessages.length / sentMessages.length) * 100) : 0,
          byDate: chartData,
        });
        
      } catch (error) {
        console.error("Error fetching SMS logs:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (partnerId) {
      fetchSMSLogs();
    }
  }, [partnerId, leads]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{metrics.totalSent}</div>
            <p className="text-sm text-muted-foreground">Απεσταλμένα SMS</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{metrics.totalClicked}</div>
            <p className="text-sm text-muted-foreground">Κλικ σε Συνδέσμους</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{metrics.clickRate}%</div>
            <p className="text-sm text-muted-foreground">Ποσοστό Κλικ</p>
            <Progress value={metrics.clickRate} className="h-2 mt-2" />
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="chart">
        <TabsList>
          <TabsTrigger value="chart">Γράφημα</TabsTrigger>
          <TabsTrigger value="list">Λίστα</TabsTrigger>
        </TabsList>
        
        <TabsContent value="chart" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Στατιστικά SMS ανά Ημέρα</CardTitle>
              <CardDescription>
                Απεσταλμένα μηνύματα και ποσοστά κλικ
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                {metrics.byDate.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={metrics.byDate}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => format(parseISO(date), 'dd MMM', { locale: el })}
                      />
                      <YAxis />
                      <Tooltip 
                        formatter={(value, name) => {
                          return [value, name === 'sent' ? 'Απεσταλμένα' : 'Κλικ'];
                        }}
                        labelFormatter={(date) => format(parseISO(date), 'dd MMMM yyyy', { locale: el })}
                      />
                      <Bar dataKey="sent" name="Απεσταλμένα" stackId="a" fill="#8884d8">
                        {metrics.byDate.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill="#8884d8" />
                        ))}
                      </Bar>
                      <Bar dataKey="clicked" name="Κλικ" stackId="b" fill="#82ca9d">
                        {metrics.byDate.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill="#82ca9d" />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground">Δεν υπάρχουν δεδομένα</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="list">
          <Card>
            <CardHeader>
              <CardTitle>Λίστα SMS</CardTitle>
              <CardDescription>
                Ιστορικό αποστολής και αλληλεπίδρασης
              </CardDescription>
            </CardHeader>
            <CardContent>
              {smsLogs.length > 0 ? (
                <div className="overflow-auto max-h-[500px]">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Ημερομηνία</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Επαφή</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Αριθμός</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Κατάσταση</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Ενέργεια</th>
                      </tr>
                    </thead>
                    <tbody>
                      {smsLogs.map((log, index) => {
                        const lead = leads.find(l => l.id === log.leadId) || { name: "Άγνωστο", phone: log.phone || "Δεν καταγράφηκε" };
                        return (
                          <tr key={index} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 text-sm">
                              {format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm', { locale: el })}
                            </td>
                            <td className="py-3 px-4 text-sm">{lead.name}</td>
                            <td className="py-3 px-4 text-sm">{lead.phone}</td>
                            <td className="py-3 px-4 text-sm">
                              {log.status === "sent" || log.messageId ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Απεστάλη
                                </span>
                              ) : log.event === "click" ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Κλικ
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  {log.status || log.event || "Άγνωστο"}
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-sm">
                              {log.event === "click" ? "Κλικ στον σύνδεσμο" : "Αποστολή SMS"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Δεν υπάρχουν καταγεγραμμένα SMS</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PartnerSMSMetrics;
